import { Scene, Choice, SceneData } from '../types/index';
import { GameState } from './GameState';
import { LLMService } from '../services/LLMService';
import { GameUI } from '../components/GameUI';
import { GAME_CONFIG } from '../config';
import { NotificationBar } from '../components/NotificationBar';

export class SceneManager {
    private gameState: GameState;
    private llmService: LLMService;
    private ui: GameUI;
    private scenes: Record<string, Scene>;

    constructor(llmService: LLMService, gameState: GameState, ui: GameUI) {
        this.llmService = llmService;
        this.gameState = gameState;
        this.ui = ui;
        this.scenes = this.initializeScenes();
    }

    private initializeScenes(): Record<string, Scene> {
        // 初始化场景定义
        return {
            start: {
                text: "2077年的夜之城，永远笼罩在霓虹与阴影之中。你站在公寓楼顶，看着远处的巨型全息广告在雨幕中闪烁。空气里混合着雨水、机油和街边小贩的拉面香气。楼下传来警笛声，一辆警车呼啸而过，但没人会多看一眼——这不过是夜之城再普通不过的一个清晨。\n\n你的神经义体刚刚完成例行校准，提示你有几条未读信息。生活总是要继续，也许是时候开始新的一天了。",
                choices: [
                    {
                        text: "下楼去丽兹酒吧喝杯咖啡，那里的老板娘总是知道些有趣的事",
                        next_scene: 'lizzies',
                        requirements: null
                    },
                    {
                        text: "去维克多诊所做个义体检查，顺便和老朋友叙叙旧",
                        next_scene: 'ripperdoc',
                        requirements: null
                    },
                    {
                        text: "漫步在日本街，呼吸着早晨的空气",
                        next_scene: 'japantown',
                        requirements: null
                    },
                    {
                        text: "去米丝蒂的店铺，让她帮我占卜一下今天的运势",
                        next_scene: 'misty',
                        requirements: null
                    },
                    {
                        text: "给杰克发条信息，问问他要不要一起吃早餐",
                        next_scene: 'contact_jackie',
                        requirements: null
                    },
                    {
                        text: "查看未读信息",
                        next_scene: 'check_messages',
                        requirements: null
                    }
                ]
            }
            // ... 其他场景定义
        };
    }

    async displayScene(
        sceneId: string, 
        previousChoice: Choice | null = null,
        isLoadingSave: boolean = false
    ): Promise<void> {
        this.ui.toggleLoading(true);

        try {
            let sceneData: SceneData;
            
            if (previousChoice) {
                // 处理玩家选择并更新对话历史
                sceneData = await this.handleChoice(previousChoice, sceneId);
                
                // 确保更新对话历史显示
                this.ui.updateDialogueHistory(this.gameState.getDialogueHistory());
            } else if (isLoadingSave && this.gameState.getCurrentSceneDescription()) {
                // 从存档加载
                sceneData = {
                    scene_text: this.gameState.getCurrentSceneDescription() || '',
                    choices: await this.generateChoicesForScene(sceneId),
                    npc_dialogue: ''
                };
            } else {
                // 初始场景或新场景
                const initialScene = this.scenes[sceneId];
                if (initialScene) {
                    // 使用预定义场景
                    sceneData = {
                        scene_text: initialScene.text,
                        choices: initialScene.choices,
                        npc_dialogue: ''
                    };
                } else {
                    // 生成新场景
                    sceneData = await this.llmService.generateDialogue(this.gameState.getState(), '');
                }
            }

            // 保存场景描述
            if (!isLoadingSave) {
                this.gameState.setCurrentSceneDescription(sceneData.scene_text);
            }

            // 更新UI
            this.ui.displayScene(sceneData);
            
            // 创建选项按钮
            sceneData.choices.forEach(choice => {
                const button = this.ui.createChoiceButton(choice, async () => {
                    this.ui.disableAllInputs();
                    await this.displayScene(choice.next_scene, choice);
                    this.ui.enableAllInputs();
                });
                this.ui.appendToChoices(button);
            });

            // 添加自定义输入
            const customInput = this.ui.createCustomInput(async (text: string) => {
                this.ui.disableAllInputs();
                const customChoice = {
                    text,
                    next_scene: sceneId,
                    requirements: null
                };
                await this.displayScene(sceneId, customChoice as Choice);
                this.ui.enableAllInputs();
            });
            this.ui.appendToChoices(customInput);

            // 添加状态显示
            const state = this.gameState.getState();
            const statusDisplay = this.ui.createStatusDisplay(
                state.inventory,
                state.relationships
            );
            this.ui.appendToChoices(statusDisplay);

            // 添加保存/加载按钮
            const saveLoadButtons = this.ui.createSaveLoadButtons(
                () => {
                    if (this.gameState.save()) {
                        this.ui.showNotification('游戏已保存！');
                    } else {
                        this.ui.showNotification('保存游戏失败！', 'error');
                    }
                },
                async () => {
                    if (this.gameState.load()) {
                        await this.displayScene(this.gameState.getState().currentScene, null, true);
                        this.ui.showNotification('游戏已加载！');
                    } else {
                        this.ui.showNotification('没有找到保存的游戏或加载失败！', 'error');
                    }
                }
            );
            this.ui.appendToChoices(saveLoadButtons);
            
        } catch (error) {
            console.error('场景显示错误:', error);
            // 不直接显示错误，而是显示重试选项
            const retryScene = {
                scene_text: "连接出现问题，请重试...",
                choices: [
                    {
                        text: "重新连接",
                        next_scene: sceneId,
                        requirements: null
                    }
                ],
                npc_dialogue: ''
            };
            this.gameState.setCurrentSceneDescription(retryScene.scene_text);
            this.ui.displayScene(retryScene);
        } finally {
            this.ui.toggleLoading(false);
        }
    }

    private async handleChoice(choice: Choice, sceneId: string, retryCount = 0): Promise<SceneData> {
        const maxRetries = 3;
        const retryDelay = 1000; // 1秒后重试

        try {
            // 更新当前场景
            this.gameState.setState({ currentScene: sceneId });

            // 生成新的对话内容
            const sceneData = await this.llmService.generateDialogue(this.gameState.getState(), choice.text);
            
            // 更新对话历史
            if (sceneData.scene_text && choice.text) {
                this.gameState.addDialogueEntry(
                    sceneId,
                    choice.text,
                    sceneData.npc_dialogue || ''
                );
                
                // 更新UI中的对话历史显示
                this.ui.updateDialogueHistory(this.gameState.getDialogueHistory());

                // 自动保存游戏
                if (this.gameState.save()) {
                    this.ui.showNotification('游戏已自动保存', 'info', 2000);
                }
            }
            
            // 如果 LLM 返回了状态变更，则更新游戏状态
            if (sceneData.state_changes) {
                this.gameState.update(sceneData.state_changes);
                
                // 显示重要的状态变更通知
                const oldInventory = new Set(this.gameState.getState().inventory);
                const newInventory = new Set(sceneData.state_changes.inventory || []);
                
                // 检查新获得的物品
                const gainedItems = [...newInventory].filter(item => !oldInventory.has(item));
                if (gainedItems.length > 0) {
                    this.ui.showNotification(`获得物品: ${gainedItems.join(', ')}`, 'success', 3000);
                }
            }

            return sceneData;
        } catch (error) {
            console.error('处理选择时出错:', error);
            
            // 如果还没有超过最大重试次数，则等待后重试
            if (retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                return this.handleChoice(choice, sceneId, retryCount + 1);
            }
            
            throw error;
        }
    }

    // 为场景生成选项
    private async generateChoicesForScene(sceneId: string): Promise<Choice[]> {
        try {
            const response = await this.llmService.generateDialogue(this.gameState.getState(), '');
            return response.choices;
        } catch (error) {
            console.error('生成场景选项失败:', error);
            return [
                {
                    text: "继续",
                    next_scene: sceneId,
                    requirements: null
                }
            ];
        }
    }
} 